import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Coordinate } from "./layer/tileLayer/GeoJsonLayer";
import { IssueGeoJsonLayer } from "./layer/tileLayer/IssueGeoJsonLayer";
import { icon } from "./utils";
import {
  computeAddGeoJson,
  computeUpdateGeoJson,
  type FeatureFromGeojson,
  type FeatureCollection,
} from "./FeaturesStore.utils";
import { ANNOTATION_MARKER_STYLE } from "src/domains/shared/constants/annotationMarkerStyle";
import { ANNOTATION_SELECT_STYLE } from "src/domains/shared/constants/annotationSelectStyle";

export type { FeatureFromGeojson, FeatureCollection };

interface FeaturesGeoJsonStore {
  geoJson: FeatureCollection;
  changeGeoJson: (geoJson: FeatureCollection) => void;
  addGeoJson: (newGeoJson: FeatureFromGeojson) => void;
  removeGeoJson: (geoJsonId: string) => void;
  getGeoJsonElement: (getJsonId: string) => FeatureFromGeojson | undefined;
  updateGeoJson: (
    id: string,
    updatedGeoJson: Omit<FeatureFromGeojson, "id">
  ) => void;
}

/**
 * GeoJSON 피처 관리 컨테이너 props — 자식에서 `useFeaturesStore()` 로 add/update/remove 액세스.
 *
 * `onChange` 가 있으면 외부 상태와 동기화, 없으면 내부 상태로만 관리.
 * `projectionCode` 는 데이터 원본 좌표계(드론 데이터는 보통 EPSG:5186 또는 EPSG:4326).
 */
interface FeaturesStoreProps {
  geoJson: FeatureCollection;
  onChange?: (geoJson: FeatureCollection) => void;
  projectionCode: Coordinate;
  children?: ReactNode;
  zIndex?: number;
}

export const FeaturesStoreContext = createContext<FeaturesGeoJsonStore | null>(
  null
);

export const useFeaturesStore = () =>
  useContext(FeaturesStoreContext) as FeaturesGeoJsonStore;

function normalizeGeoJson(geoJson: FeatureCollection): FeatureCollection {
  return {
    type: "FeatureCollection",
    features: (geoJson?.features || []).map((f) => ({
      ...f,
      properties: {
        ...(f.properties || {}),
        isSelected: !!f.properties?.isSelected,
        isModifying: !!f.properties?.isModifying,
        isMoving: !!f.properties?.isMoving,
      },
    })),
  };
}

export function FeaturesStore({
  children,
  geoJson,
  projectionCode,
  onChange,
  zIndex = 1,
}: FeaturesStoreProps) {
  const [geoJsonState, setGeoJson] = useState<FeatureCollection>({
    type: "FeatureCollection",
    features: [],
  });

  const geoJsonStateRef = useRef(geoJsonState);
  geoJsonStateRef.current = geoJsonState;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // 아이콘 경로 초기화 — 마운트 시 1회
  useEffect(() => {
    icon.marker = ANNOTATION_MARKER_STYLE.icon;
    icon.selected = ANNOTATION_SELECT_STYLE.markerSelectedIcon;
  }, []);

  // 외부 geoJson prop → 내부 상태 동기화
  useEffect(() => {
    setGeoJson(normalizeGeoJson(geoJson));
  }, [geoJson]);

  // onChange가 있으면 외부로 위임, 없으면 내부 state 업데이트
  const commitGeoJson = useCallback((updated: FeatureCollection) => {
    if (onChangeRef.current) {
      onChangeRef.current(updated);
    } else {
      setGeoJson(updated);
    }
  }, []);

  const changeGeoJson = commitGeoJson;

  const addGeoJson = useCallback((newGeoJson: FeatureFromGeojson) => {
    const updated = computeAddGeoJson(geoJsonStateRef.current.features, newGeoJson);
    commitGeoJson(updated);
  }, [commitGeoJson]);

  const updateGeoJson = useCallback(
    (id: string, updatedGeoJson: Omit<FeatureFromGeojson, "id">) => {
      const updated = computeUpdateGeoJson(geoJsonStateRef.current.features, id, updatedGeoJson);
      commitGeoJson(updated);
    },
    [commitGeoJson]
  );

  const removeGeoJson = useCallback((geoJsonId: string) => {
    const current = geoJsonStateRef.current;
    const updated: FeatureCollection = {
      ...current,
      features: current.features.filter((f) => f.id !== geoJsonId),
    };
    commitGeoJson(updated);
  }, [commitGeoJson]);

  const getGeoJsonElement = useCallback((geoJsonId: string) => {
    return geoJsonStateRef.current.features.find(
      (feature) => feature.id === geoJsonId
    );
  }, []);

  const provideValues = useMemo(
    () => ({
      geoJson: geoJsonState,
      changeGeoJson,
      addGeoJson,
      removeGeoJson,
      updateGeoJson,
      getGeoJsonElement,
    }),
    [
      geoJsonState,
      changeGeoJson,
      addGeoJson,
      removeGeoJson,
      updateGeoJson,
      getGeoJsonElement,
    ]
  );

  return (
    <FeaturesStoreContext.Provider value={provideValues}>
      <IssueGeoJsonLayer
        zIndex={zIndex}
        geoJson={geoJsonState}
        projectionCode={projectionCode}
      />
      {children}
    </FeaturesStoreContext.Provider>
  );
}
